/**
 * @file 二维码倒计时 hook
 * @description 根据服务端下发的过期时间进行本地倒计时，到期触发 EXPIRED 事件
 */

import {useEffect, useRef} from 'react';

/**
 * 二维码倒计时 hook
 * - expireAt 由服务端下发（Unix 秒，调用方负责统一为 ms）
 * - 到时触发 onExpire 回调（dispatch EXPIRED）
 * - expireAt 变化时自动 cleanup + 重新计时
 */
export function useQRCountdown(expireAt: number | null, onExpire: () => void) {
    // 用 ref 持有最新回调，避免 onExpire 引用变化导致定时器被误清
    const onExpireRef = useRef(onExpire);
    onExpireRef.current = onExpire;

    useEffect(() => {
        if (!expireAt) return () => {}; // idle/creating 态下没有 order，跳过

        // 服务端下发的是 Unix 秒，转为毫秒
        const expireAtMs = expireAt * 1000;
        const remaining = expireAtMs - Date.now();

        if (remaining <= 0) {
            // 已过期（罕见：刚下单就已经过期，通常是服务器时钟问题）
            onExpireRef.current();
            return () => {};
        }

        const timer = setTimeout(() => onExpireRef.current(), remaining);
        return () => clearTimeout(timer); // 卸载 / expireAt 变化时自动清理
    }, [expireAt]);
}
